import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Save } from 'lucide-react';
import { api } from '../hooks/api.js';
import { useEditorStore } from '../stores/editorStore.js';
import { cloneBlocksWithNewIds, tableSummary } from '../helpers.js';
import { SectionBlockSchema, type Block } from '@blogforge/shared';
import type { DragPath } from '../types.js';
import { ulid } from 'ulid';

interface Props {
  /** Editor we are saving from — drives the default target binding. */
  contentKind: 'post' | 'email';
  onClose: () => void;
}

type Status = { kind: 'idle' } | { kind: 'saving' } | { kind: 'error'; message: string };

/** The block the canvas had selected when the modal opened, and where it sits. */
interface Anchor {
  block: Block;
  path: DragPath;
}

/** Where the component's blocks come from. */
type Source = 'selection' | 'document';

/**
 * For a selected columns row: save the row itself (`'whole'`), or one of its
 * columns by index — a column's blocks are saved on their own, without the row
 * that spaced them out.
 */
type ColumnsPick = 'whole' | number;

/**
 * "Save as component" — turns the editor's current selection into a reusable
 * component (declared for posts, emails or both). The blocks are stored with
 * fresh ids; documents that later insert the component get their own snapshot
 * copy.
 *
 * What gets saved is whatever the author had selected on the canvas, not the
 * whole document: the selected block on its own. A selected `columns` row is
 * the one case that needs a decision, so it offers both readings — the row as
 * it stands, or just one of its columns (optionally still held together by a
 * section). With nothing selected the modal falls back to ticking any of the
 * document's root blocks.
 */
export function SaveComponentModal({ contentKind, onClose }: Props) {
  const blocks = useEditorStore((s) => s.blocks);
  const qc = useQueryClient();

  // Read the selection once, when the modal opens: the canvas cannot change
  // underneath it, and re-targeting the save from inside the modal (a column
  // chip is a click, not a canvas selection) would be a surprise.
  const [anchor] = useState<Anchor | null>(readSelection);
  const [source, setSource] = useState<Source>(anchor ? 'selection' : 'document');
  const [columnsPick, setColumnsPick] = useState<ColumnsPick>('whole');
  const [wrapColumn, setWrapColumn] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(() => new Set(blocks.map((b) => b.id)));
  const [name, setName] = useState('');
  const [target, setTarget] = useState<'post' | 'email' | 'both'>(contentKind);
  const [status, setStatus] = useState<Status>({ kind: 'idle' });

  const columns = useMemo(() => (anchor ? columnsOf(anchor.block) : null), [anchor]);
  /** First column that actually holds blocks — what "one column only" picks by default. */
  const firstFilled = columns ? columns.lists.findIndex((list) => list.length > 0) : -1;

  const chosen = useMemo(() => {
    if (source === 'document' || !anchor) return blocks.filter((b) => selected.has(b.id));
    if (columns && columnsPick !== 'whole') {
      const column = columns.lists[columnsPick] ?? [];
      // A column is a layout container, so exporting one on its own can keep a
      // container around its blocks. An empty column is never wrapped: a
      // section holding no blocks is not worth saving.
      return wrapColumn && column.length > 0 ? [sectionAround(column)] : column;
    }
    return [anchor.block];
  }, [anchor, blocks, columns, columnsPick, selected, source, wrapColumn]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (chosen.length === 0 || name.trim().length === 0) return;
    setStatus({ kind: 'saving' });
    try {
      await api.components.create({
        name: name.trim(),
        target,
        blocks: cloneBlocksWithNewIds(chosen),
      });
      void qc.invalidateQueries({ queryKey: ['components'] });
      onClose();
    } catch (err) {
      setStatus({
        kind: 'error',
        message: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const inputCls =
    'field-input' + (status.kind === 'error' ? ' border-danger/30 focus:border-danger/50' : '');
  const pickingColumn = source === 'selection' && anchor !== null;
  const columnMode = pickingColumn && columns !== null;

  return (
    <div
      className="modal-backdrop"
      onClick={() => {
        if (status.kind !== 'saving') onClose();
      }}
    >
      <div className="modal-card w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-ink text-[17px] font-bold tracking-tight">Save as component</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={status.kind === 'saving'}
            className="text-muted hover:bg-card-2 hover:text-ink-soft rounded p-1 disabled:opacity-40"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSave} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-muted mb-1 block text-xs font-medium">Component name</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Pricing section"
                autoFocus
                className={inputCls}
              />
            </div>
            <div>
              <label className="text-muted mb-1 block text-xs font-medium">Use in</label>
              <select
                value={target}
                onChange={(e) => setTarget(e.target.value as 'post' | 'email' | 'both')}
                className="field-input"
              >
                <option value="post">Posts only</option>
                <option value="email">Emails only</option>
                <option value="both">Posts &amp; emails</option>
              </select>
            </div>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className="text-muted text-xs font-medium">
                {pickingColumn
                  ? 'Block to save'
                  : `Blocks to include (${chosen.length}/${blocks.length})`}
              </p>
              {/* Only offered when there is a selection to switch away from. */}
              {anchor && (
                <div className="border-line flex flex-none gap-0.5 rounded-md border p-0.5">
                  {(
                    [
                      ['selection', 'Selected block'],
                      ['document', 'Whole document'],
                    ] as const
                  ).map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setSource(value)}
                      className={
                        'rounded px-2 py-0.5 text-[11px] font-medium transition-colors ' +
                        (source === value
                          ? 'bg-accent text-accent-ink'
                          : 'text-muted hover:text-ink-soft')
                      }
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {pickingColumn ? (
              <div className="space-y-3">
                <div className="border-line bg-bg-soft flex items-center gap-2 rounded-md border px-2 py-1.5">
                  <span className="bg-card-2 text-muted w-16 flex-none rounded px-1.5 py-0.5 text-center text-[10px] font-semibold tracking-wide uppercase">
                    {anchor.block.type}
                  </span>
                  <span className="text-ink-soft flex-1 truncate text-xs">
                    {blockTextPreview(anchor.block)}
                  </span>
                  {anchor.path.length > 0 && (
                    <span className="text-muted flex-none text-[10px]">nested</span>
                  )}
                </div>

                {columnMode && (
                  <div className="space-y-2">
                    <p className="text-muted text-xs">
                      This block is a columns row. Save it whole, or pick one column to save on its
                      own — the column&apos;s blocks arrive without the row around them.
                    </p>
                    <label className="text-ink-soft flex cursor-pointer items-center gap-2 text-xs">
                      <input
                        type="radio"
                        name="columns-pick"
                        checked={columnsPick === 'whole'}
                        onChange={() => setColumnsPick('whole')}
                        className={radioCls}
                      />
                      Save as-is — the whole row, columns included
                    </label>
                    <label
                      className={
                        'flex items-center gap-2 text-xs ' +
                        (firstFilled === -1
                          ? 'text-muted cursor-not-allowed'
                          : 'text-ink-soft cursor-pointer')
                      }
                    >
                      <input
                        type="radio"
                        name="columns-pick"
                        checked={columnsPick !== 'whole'}
                        disabled={firstFilled === -1}
                        onChange={() => setColumnsPick(firstFilled)}
                        className={radioCls}
                      />
                      One column only
                    </label>
                    <div className="grid grid-cols-3 gap-1.5 pl-5">
                      {Array.from({ length: columns.count }, (_, i) => {
                        const count = (columns.lists[i] ?? []).length;
                        const active = columnsPick === i;
                        return (
                          <button
                            key={i}
                            type="button"
                            disabled={count === 0}
                            onClick={() => setColumnsPick(i)}
                            className={
                              'rounded-md border px-2 py-1.5 text-[11px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 ' +
                              (active
                                ? 'border-accent-border bg-accent/10 text-accent'
                                : 'border-line text-ink-soft hover:bg-bg-soft')
                            }
                          >
                            Column {i + 1}
                            <span className="text-muted block text-[10px] font-normal">
                              {count === 0 ? 'empty' : `${count} block${count === 1 ? '' : 's'}`}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    {columnsPick !== 'whole' && (
                      <label className="text-ink-soft flex cursor-pointer items-start gap-2 pl-5 text-xs">
                        <input
                          type="checkbox"
                          checked={wrapColumn}
                          onChange={(e) => setWrapColumn(e.target.checked)}
                          className={checkboxCls + ' mt-0.5'}
                        />
                        <span>
                          Wrap the column in a section
                          <span className="text-muted block text-[10px]">
                            The component arrives as one block that carries its own padding and
                            background — otherwise its blocks land loose in the document.
                          </span>
                        </span>
                      </label>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="border-line max-h-56 space-y-1 overflow-y-auto rounded-lg border p-2">
                {blocks.length === 0 && (
                  <p className="text-muted px-2 py-3 text-center text-xs">
                    Nothing to save yet — add some blocks first.
                  </p>
                )}
                {blocks.map((block, i) => (
                  <label
                    key={block.id}
                    className="hover:bg-bg-soft flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(block.id)}
                      onChange={() => toggle(block.id)}
                      className={checkboxCls}
                    />
                    <span className="text-muted w-6 flex-none text-center text-xs">{i + 1}</span>
                    <span className="bg-card-2 text-muted w-16 flex-none rounded px-1.5 py-0.5 text-center text-[10px] font-semibold tracking-wide uppercase">
                      {block.type}
                    </span>
                    <span className="text-ink-soft truncate text-xs">
                      {blockTextPreview(block)}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {status.kind === 'error' && <p className="text-danger text-xs">{status.message}</p>}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={status.kind === 'saving'}
              className="btn-ghost px-3 py-1.5 text-xs disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={status.kind === 'saving' || chosen.length === 0 || !name.trim()}
              className="btn-primary px-3 py-1.5 text-xs disabled:opacity-40"
            >
              <Save size={13} />
              {status.kind === 'saving' ? 'Saving…' : 'Save component'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const radioCls = 'text-accent focus:ring-accent h-3.5 w-3.5 border-line-strong';

/**
 * The selection the canvas is holding, resolved against the current document.
 * Read from `getState()` rather than a hook: the modal resolves it once, on
 * mount, instead of re-rendering (and re-targeting the save) whenever the
 * canvas selection moves.
 */
function readSelection(): Anchor | null {
  const { selectedBlockId, blocks, getBlockInPath } = useEditorStore.getState();
  if (!selectedBlockId) return null;
  const [id, path] = selectedBlockId;
  const block = path.length === 0 ? blocks.find((b) => b.id === id) : getBlockInPath(id, path);
  return block ? { block, path } : null;
}

/**
 * A selected block's columns, when it is a columns row: the per-column block
 * lists plus the column count to show. The declared count can outrun the lists
 * (a column added but not filled yet), so the larger of the two wins and the
 * missing entries read as empty lists.
 */
function columnsOf(block: Block): { lists: Block[][]; count: number } | null {
  if (block.type !== 'columns') return null;
  const lists = (block.attrs.columnBlocks as Block[][] | undefined) ?? [];
  const count = Math.max(block.attrs.columns ?? 0, lists.length, 1);
  return { lists, count };
}

/**
 * A column's blocks inside a fresh section, for the "wrap the column in a
 * section" option. The column was a layout container, and this keeps its
 * contents grouped — as one block that can carry padding, a background and a
 * content width — instead of dropping them loose into whatever document inserts
 * the component. The section's own attrs come from `SectionBlockSchema`; unlike
 * the adder's drafts, this is parsed, so the schema's defaults apply and the
 * shape here cannot drift from it.
 */
function sectionAround(blocks: Block[]): Block {
  return SectionBlockSchema.parse({ id: ulid(), type: 'section', content: '', attrs: { blocks } });
}

/** Shared styling for the modal's tick boxes. */
const checkboxCls = 'text-accent focus:ring-accent border-line-strong h-3.5 w-3.5 rounded';

function blockTextPreview(block: Block): string {
  // Containers hold no prose of their own — describe the shape instead, so a
  // selected section or columns row is not summarised as "(empty …)".
  if (block.type === 'columns') {
    const { lists, count } = columnsOf(block)!;
    const filled = lists.filter((list) => list.length > 0).length;
    return `${count} column${count === 1 ? '' : 's'} · ${filled} filled`;
  }
  if (block.type === 'section') {
    const nested = ((block.attrs.blocks as Block[] | undefined) ?? []).length;
    return `${nested} block${nested === 1 ? '' : 's'}`;
  }
  if (block.type === 'accordion') {
    const items = block.attrs.items?.length ?? 0;
    return `${items} item${items === 1 ? '' : 's'}`;
  }
  // A list block has no `content` of its own: it holds a template.
  const raw = 'content' in block ? (block.content ?? '') : '';
  const text = raw.trim() || blockTextFromAttrs(block);
  const plain = text.replace(/<[^>]*>/g, '');
  return plain.length > 80 ? `${plain.slice(0, 80)}…` : plain || `(empty ${block.type})`;
}

function blockTextFromAttrs(block: Block): string {
  if (block.type === 'image') return (block.attrs as { alt?: string }).alt ?? '';
  if (block.type === 'button') return (block.attrs as { href?: string }).href ?? '';
  if (block.type === 'html') return 'Custom HTML';
  // A table's `content` is only its heading, so show the data's shape instead.
  if (block.type === 'table') return tableSummary(block);
  return '';
}
